'use client';

import { useState, useEffect } from 'react';
import { checkIsAdminAction } from '@/server/sitemap/check-admin-action';
import { runMigrationAction, testSingleDocumentAction } from '@/server/migrate/migrate-actions';
import { RegionCode } from 'shared/src/types/region.type';
import { MigrationTestResults } from '@/components/admin/migration-test-results';
import { MigrationTestSuiteResult } from '@/server/migrate/__tests__/migration-test-suite';

interface MigrationResult {
  success: boolean;
  data?: any;
  message?: string;
  error?: string;
  action?: string;
  timestamp?: Date;
}

// Available regions for migration
const AVAILABLE_REGIONS = [
  { code: RegionCode.Canada, name: 'Canada', emoji: '🇨🇦' },
  { code: RegionCode.USA, name: 'USA', emoji: '🇺🇸' },
  { code: RegionCode.France, name: 'France', emoji: '🇫🇷' },
  { code: RegionCode.UK, name: 'UK', emoji: '🇬🇧' },
  { code: RegionCode.Australia, name: 'Australia', emoji: '🇦🇺' },
];

export const UniversalRegionMigrationTool = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDev, setIsDev] = useState(false);
  const [migrationResults, setMigrationResults] = useState<MigrationResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [testDocumentId, setTestDocumentId] = useState('144hz-monitors-en-CA');
  const [currentAction, setCurrentAction] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sourceRegion, setSourceRegion] = useState<RegionCode>(RegionCode.Canada); // Default source
  const [destinationRegion, setDestinationRegion] = useState<RegionCode>(RegionCode.USA); // Default destination
  const [isSourceDropdownOpen, setIsSourceDropdownOpen] = useState(false);
  const [isDestinationDropdownOpen, setIsDestinationDropdownOpen] = useState(false);
  const [isConfirmingMigration, setIsConfirmingMigration] = useState(false);
  const [updatePageContent, setUpdatePageContent] = useState(false);
  const [updateSearchTerms, setUpdateSearchTerms] = useState(false);
  const [forceOverwrite, setForceOverwrite] = useState(false);
  const [forceOverwriteNonEnglish, setForceOverwriteNonEnglish] = useState(false);
  const [testResults, setTestResults] = useState<MigrationTestSuiteResult | null>(null);
  const [isRunningTests, setIsRunningTests] = useState(false);
  const [showTestResults, setShowTestResults] = useState(false);
  const [isSplitButtonExpanded, setIsSplitButtonExpanded] = useState(false);
  const [migrateTranslationsOnly, setMigrateTranslationsOnly] = useState(false);

  // Check if we're in development environment and user is admin
  useEffect(() => {
    const checkAccess = async () => {
      try {
        const adminStatus = await checkIsAdminAction();
        const devMode = process.env.NODE_ENV === 'development';

        setIsAdmin(adminStatus);
        setIsDev(devMode);
        setIsLoading(false);
      } catch (error) {
        console.error('Failed to check admin status:', error);
        setIsAdmin(false);
        setIsDev(false);
        setIsLoading(false);
      }
    };

    checkAccess();
  }, []);

  // Handle escape key to close modal and dropdowns
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isSplitButtonExpanded) {
          setIsSplitButtonExpanded(false);
          setIsConfirmingMigration(false);
        } else {
          setIsModalOpen(false);
          setIsSourceDropdownOpen(false);
          setIsDestinationDropdownOpen(false);
          setIsConfirmingMigration(false);
        }
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Element;
      if (!target.closest('.dropdown-container')) {
        setIsSourceDropdownOpen(false);
        setIsDestinationDropdownOpen(false);
      }
      // Only cancel confirmation/expansion if clicking outside the button
      if (!target.closest('.migration-button-container')) {
        if (isSplitButtonExpanded) {
          setIsSplitButtonExpanded(false);
        }
        if (isConfirmingMigration) {
          setIsConfirmingMigration(false);
        }
      }
    };

    if (isModalOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [isModalOpen, isConfirmingMigration, isSplitButtonExpanded]);

  const addResult = (result: MigrationResult, action: string) => {
    const newResult = {
      ...result,
      action,
      timestamp: new Date(),
    };

    // Add specific success messages based on the action and result
    if (result.success && !result.message) {
      if (action.startsWith('Test Document:')) {
        newResult.message = `Document successfully migrated from ${sourceRegion} to ${destinationRegion} format`;
      } else if (action === 'Test Migration') {
        newResult.message = `Test migration completed successfully (${sourceRegion} → ${destinationRegion}) - no production data was affected`;
      } else if (action === 'Full Migration') {
        const count = result.data?.successful || 0;
        newResult.message = `Successfully migrated ${count} documents from ${sourceRegion} to ${destinationRegion} format`;
      }
    }

    setMigrationResults((prev) => [newResult, ...prev]);
  };

  const clearResults = () => {
    setMigrationResults([]);
  };

  const executeAction = async (actionFn: () => Promise<any>, actionName: string) => {
    setIsRunning(true);
    setCurrentAction(actionName);
    try {
      const result = await actionFn();
      addResult(result, actionName);
    } catch (error) {
      addResult(
        {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error occurred',
        },
        actionName,
      );
    } finally {
      setIsRunning(false);
      setCurrentAction('');
    }
  };

  const handleRunMigration = (translationsOnly: boolean = false) => {
    const actionName = translationsOnly ? 'Translations Migration' : 'Full Migration';
    return executeAction(
      () => runMigrationAction(sourceRegion, destinationRegion, updatePageContent, updateSearchTerms, forceOverwrite, forceOverwriteNonEnglish, translationsOnly),
      actionName,
    );
  };

  const handleTestSingleDocument = async () => {
    if (!testDocumentId.trim()) {
      addResult(
        {
          success: false,
          error: `Please enter a document ID (should end with -${sourceRegion}, e.g., "beauty-personal-care-${sourceRegion}")`,
        },
        'Test Single Document',
      );
      return;
    }
    await executeAction(
      () =>
        testSingleDocumentAction(
          testDocumentId.trim(),
          sourceRegion,
          destinationRegion,
          updatePageContent,
          updateSearchTerms,
          forceOverwrite,
          forceOverwriteNonEnglish,
        ),
      `Test Document: ${testDocumentId}`,
    );
  };

  const handleTestBothLanguages = async () => {
    const baseId = '144hz-monitors';
    setCurrentAction('Testing Both Languages (English-First Strategy)');
    setIsRunning(true);

    try {
      // Test English first
      addResult(
        { success: true, message: 'Starting English-first migration test...' },
        'English-First Strategy',
      );

      const enResult = await testSingleDocumentAction(
        `${baseId}-en-${sourceRegion}`,
        sourceRegion,
        destinationRegion,
        updatePageContent,
        updateSearchTerms,
        forceOverwrite,
        forceOverwriteNonEnglish,
      );
      
      addResult(enResult, `Test English: ${baseId}-en-${sourceRegion}`);

      // Small delay to ensure cache is updated
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Then test French (should use cached English products if updatePageContent is enabled)
      const frResult = await testSingleDocumentAction(
        `${baseId}-fr-${sourceRegion}`,
        sourceRegion,
        destinationRegion,
        updatePageContent,
        updateSearchTerms,
        forceOverwrite,
        forceOverwriteNonEnglish,
      );

      addResult(frResult, `Test French: ${baseId}-fr-${sourceRegion}`);

      if (enResult.success && frResult.success && updatePageContent) {
        addResult(
          {
            success: true,
            message: 'Both languages migrated! Check console logs - French should show "Product Cache HIT" if English products were reused.',
          },
          'English-First Strategy Complete',
        );
      }
    } catch (error) {
      addResult(
        {
          success: false,
          error: error instanceof Error ? error.message : 'Both languages test failed',
        },
        'Test Both Languages',
      );
    } finally {
      setIsRunning(false);
      setCurrentAction('');
    }
  };

  const handleRunTestSuite = async () => {
    setIsRunningTests(true);
    setShowTestResults(true);
    setTestResults(null);

    try {
      // Send the current migration configuration to test
      const response = await fetch('/api/admin/run-migration-tests', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sourceRegion,
          destinationRegion,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setTestResults(data.results);
        
        // Add a summary to migration results
        addResult(
          {
            success: data.results.summary.overallPassed,
            message: data.results.summary.overallPassed
              ? `Test Suite: All ${data.results.passed} tests passed for ${sourceRegion} → ${destinationRegion}`
              : `Test Suite: ${data.results.failed} of ${data.results.totalTests} tests failed`,
            data: {
              passed: data.results.passed,
              failed: data.results.failed,
              totalTests: data.results.totalTests,
            },
          },
          `Test Suite: ${sourceRegion} → ${destinationRegion}`,
        );
      } else {
        addResult(
          {
            success: false,
            error: data.error || 'Failed to run test suite',
          },
          'Test Suite',
        );
      }
    } catch (error) {
      addResult(
        {
          success: false,
          error: error instanceof Error ? error.message : 'Failed to run test suite',
        },
        'Test Suite',
      );
    } finally {
      setIsRunningTests(false);
    }
  };

  // Don't render if not in dev mode or not admin
  if (isLoading) {
    return <></>;
  }

  if (!isDev || !isAdmin) {
    return null;
  }

  return (
    <>
      {/* Migration Button */}
      <div className="mx-auto max-w-5xl pt-8">
        <button
          onClick={() => setIsModalOpen(true)}
          className="group relative w-full max-w-2xl overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 px-10 py-8 text-white shadow-2xl transition-all duration-500 hover:scale-[1.02] hover:shadow-[0_20px_50px_rgba(59,130,246,0.4)] active:scale-[0.98]"
        >
          {/* Animated gradient border effect */}
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-400 opacity-0 blur-xl transition-opacity duration-500 group-hover:opacity-40"></div>
          
          {/* Glass-morphism overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/[0.08] to-transparent"></div>
          
          {/* Subtle grid pattern */}
          <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(circle_at_50%_50%,white_1px,transparent_1px)] bg-[length:20px_20px]"></div>
          
          {/* Animated shimmer effect */}
          <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent transition-transform duration-1000 group-hover:translate-x-full"></div>
          
          <div className="relative flex items-center justify-center space-x-5">
            {/* Icon container with enhanced styling */}
            <div className="relative flex h-14 w-14 items-center justify-center">
              {/* Pulsing background glow */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-400 to-cyan-400 opacity-20 blur-md transition-all duration-500 group-hover:opacity-40 group-hover:scale-110"></div>
              
              {/* Icon background */}
              <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500/30 to-cyan-500/30 backdrop-blur-sm border border-white/10 shadow-lg transition-all duration-500 group-hover:border-white/20 group-hover:shadow-blue-500/50">
                <svg className="h-7 w-7 text-white drop-shadow-lg transition-transform duration-500 group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                </svg>
              </div>
            </div>
            
            {/* Text content */}
            <div className="text-left">
              <h2 className="text-2xl font-bold tracking-tight text-white drop-shadow-lg transition-all duration-300 group-hover:tracking-wide">
                Migration Tools
              </h2>
              <p className="mt-1 text-sm font-medium text-blue-200/80 transition-colors duration-300 group-hover:text-cyan-200">
                Advanced Region Configuration
              </p>
            </div>
            
            {/* Chevron indicator */}
            <div className="ml-auto opacity-0 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-1">
              <svg className="h-6 w-6 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
          
          {/* Bottom accent line */}
          <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-blue-400 to-transparent opacity-50 transition-opacity duration-300 group-hover:opacity-100"></div>
        </button>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-200/80 dark:bg-dark/80 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsModalOpen(false);
            }
          }}
        >
          <div className="mx-4 max-h-[92vh] max-sm:max-h-[82vh] mt-12 max-sm:mt-20 w-full max-w-5xl overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-gray-800 flex flex-col">
            {/* Modal Header */}
            <div className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 px-8 py-5 flex-shrink-0 overflow-hidden">
              {/* Glass-morphism overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/[0.08] to-transparent"></div>
              
              {/* Subtle grid pattern */}
              <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(circle_at_50%_50%,white_1px,transparent_1px)] bg-[length:20px_20px]"></div>
              
              <div className="relative flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {/* Icon container with enhanced styling matching button */}
                  <div className="relative flex h-12 w-12 items-center justify-center">
                    {/* Pulsing background glow */}
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-400 to-cyan-400 opacity-20 blur-md"></div>
                    
                    {/* Icon background */}
                    <div className="relative flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500/30 to-cyan-500/30 backdrop-blur-sm border border-white/10 shadow-lg">
                      <svg className="h-7 w-7 text-white drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                      </svg>
                    </div>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold tracking-tight text-white drop-shadow-lg">Migration Tools</h2>
                    <p className="text-blue-100">
                      {sourceRegion && destinationRegion ? `${sourceRegion} → ${destinationRegion}` : 'Select regions to migrate'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="rounded-full bg-white/20 p-2 text-white backdrop-blur-sm transition-all hover:bg-white/30"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Bottom accent line */}
              <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-blue-400 to-transparent opacity-50"></div>
            </div>

            {/* Modal Content */}
            <div className="overflow-y-auto px-6 py-3 bg-white dark:bg-dark/80 flex-1 min-h-0">
              {/* Region Selection */}
              <div className="mb-4">
                <div className="rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 p-4 shadow-sm border dark:from-slate-800 dark:to-slate-900 dark:border-slate-700 relative pb-20 max-sm:pb-40">
                  <div className="flex items-center justify-center space-x-8 max-sm:flex-col max-sm:space-x-0 max-sm:space-y-6">
                    {/* Source Country Dropdown */}
                    <div className="text-center relative dropdown-container">
                      <p className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-3">FROM</p>
                      <div className="relative">
                        <button
                          onClick={() => setIsSourceDropdownOpen(!isSourceDropdownOpen)}
                          className="px-6 py-4 rounded-xl transition-all duration-200 border-2 border-blue-500 bg-blue-50 shadow-lg dark:border-blue-400 dark:bg-blue-900/30 min-w-[200px] max-sm:py-3 max-sm:px-4"
                        >
                          <div className="flex items-center justify-between space-x-3">
                            <div className="flex items-center space-x-3">
                              <span className="text-3xl max-sm:text-2xl">
                                {AVAILABLE_REGIONS.find((r) => r.code === sourceRegion)?.emoji}
                              </span>
                              <span className="text-xl font-semibold text-blue-700 dark:text-blue-300 max-sm:text-lg">
                                {AVAILABLE_REGIONS.find((r) => r.code === sourceRegion)?.name}
                              </span>
                            </div>
                            <svg
                              className={`w-5 h-5 text-blue-700 dark:text-blue-300 transition-transform ${isSourceDropdownOpen ? 'rotate-180' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </button>

                        {isSourceDropdownOpen && (
                          <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl shadow-xl z-50 max-h-64 max-sm:max-h-48 overflow-y-auto">
                            {AVAILABLE_REGIONS.map((region) => (
                              <button
                                key={region.code}
                                onClick={() => {
                                  setSourceRegion(region.code);
                                  setIsSourceDropdownOpen(false);
                                  if (destinationRegion === region.code) {
                                    setDestinationRegion('' as RegionCode);
                                  }
                                }}
                                className={`w-full px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors first:rounded-t-xl last:rounded-b-xl ${
                                  sourceRegion === region.code ? 'bg-blue-50 dark:bg-blue-900/30' : ''
                                }`}
                              >
                                <div className="flex items-center space-x-3">
                                  <span className="text-2xl">{region.emoji}</span>
                                  <span className="text-lg font-medium text-slate-700 dark:text-slate-300">{region.name}</span>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Arrow */}
                    <div className="flex flex-col items-center sm:mt-auto">
                      <div className="relative flex h-14 w-14 items-center justify-center">
                        {sourceRegion && destinationRegion && (
                          <>
                            {/* Pulsing background glow */}
                            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-400 to-cyan-400 opacity-30 blur-md transition-all duration-500 animate-pulse"></div>
                          </>
                        )}
                        
                        {/* Icon background - simplified and less transparent */}
                        <div className={`relative px-4 mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border shadow-lg transition-all duration-300 ${
                          sourceRegion && destinationRegion
                            ? 'bg-gradient-to-br from-blue-500/60 to-cyan-500/60 border-white/20'
                            : 'bg-slate-200 border-slate-300 dark:bg-slate-700 dark:border-slate-600'
                        }`}>
                          <svg className={`w-7 h-7 transition-all duration-300 ${
                            sourceRegion && destinationRegion
                              ? 'text-white drop-shadow-lg'
                              : 'text-slate-400 dark:text-slate-500'
                          }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2.5}
                              d="M17 8l4 4m0 0l-4 4m4-4H3"
                              className="max-sm:hidden"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2.5}
                              d="M12 5v14m0 0l-4-4m4 4l4-4"
                              className="hidden max-sm:block"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Destination Country Dropdown */}
                    <div className="text-center relative dropdown-container !mt-2">
                      <p className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-3">TO</p>
                      <div className="relative">
                        <button
                          onClick={() => {
                            if (!sourceRegion) {
                              // If no source selected, open source dropdown
                              setIsSourceDropdownOpen(true);
                            } else {
                              // If source is selected, toggle destination dropdown
                              setIsDestinationDropdownOpen(!isDestinationDropdownOpen);
                            }
                          }}
                          className={`px-6 py-4 rounded-xl transition-all duration-200 border-2 min-w-[200px] max-sm:py-3 max-sm:px-4 ${
                            !sourceRegion
                              ? 'border-slate-300 bg-white text-slate-500 hover:border-blue-300 hover:bg-blue-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400 dark:hover:border-blue-400 dark:hover:bg-blue-900/20'
                              : destinationRegion
                                ? 'border-blue-600 bg-blue-100 shadow-lg dark:border-blue-500 dark:bg-blue-800/40'
                                : 'border-slate-300 bg-white hover:border-blue-300 hover:bg-blue-50 dark:border-slate-600 dark:bg-slate-800 dark:hover:border-blue-400 dark:hover:bg-blue-900/20'
                          }`}
                        >
                          <div className="flex items-center justify-between space-x-3">
                            {destinationRegion ? (
                              <div className="flex items-center space-x-3">
                                <span className="text-3xl max-sm:text-2xl">
                                  {AVAILABLE_REGIONS.find((r) => r.code === destinationRegion)?.emoji}
                                </span>
                                <span className="text-xl font-semibold text-blue-700 dark:text-blue-300 max-sm:text-lg">
                                  {AVAILABLE_REGIONS.find((r) => r.code === destinationRegion)?.name}
                                </span>
                              </div>
                            ) : (
                              <span className="text-lg text-slate-500 dark:text-slate-400 max-sm:text-base">
                                {!sourceRegion ? 'Select source first' : 'Choose destination'}
                              </span>
                            )}
                            <svg
                              className={`w-5 h-5 transition-transform ${
                                !sourceRegion
                                  ? 'text-slate-400 dark:text-slate-500'
                                  : destinationRegion
                                    ? 'text-blue-700 dark:text-blue-300'
                                    : 'text-slate-500 dark:text-slate-400'
                              } ${isDestinationDropdownOpen ? 'rotate-180' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </button>

                        {isDestinationDropdownOpen && sourceRegion && (
                          <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl shadow-xl z-50 max-h-64 max-sm:max-h-48 overflow-y-auto">
                            {AVAILABLE_REGIONS.filter((region) => region.code !== sourceRegion).map((region) => (
                              <button
                                key={region.code}
                                onClick={() => {
                                  setDestinationRegion(region.code);
                                  setIsDestinationDropdownOpen(false);
                                }}
                                className={`w-full px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors first:rounded-t-xl last:rounded-b-xl ${
                                  destinationRegion === region.code ? 'bg-blue-50 dark:bg-blue-900/30' : ''
                                }`}
                              >
                                <div className="flex items-center space-x-3">
                                  <span className="text-2xl">{region.emoji}</span>
                                  <span className="text-lg font-medium text-slate-700 dark:text-slate-300">{region.name}</span>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {sourceRegion && destinationRegion && (
                    <div className="mt-8 text-center">
                      <div className="relative inline-flex items-center space-x-3 overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 px-6 py-3 text-white shadow-2xl">
                        {/* Glass-morphism overlay */}
                        <div className="absolute inset-0 bg-gradient-to-br from-white/[0.08] to-transparent"></div>
                        
                        {/* Subtle grid pattern */}
                        <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(circle_at_50%_50%,white_1px,transparent_1px)] bg-[length:20px_20px]"></div>
                        
                        <div className="relative flex items-center space-x-3">
                          <span className="text-lg font-bold tracking-tight drop-shadow-lg">
                            {AVAILABLE_REGIONS.find((r) => r.code === sourceRegion)?.emoji}
                            {AVAILABLE_REGIONS.find((r) => r.code === sourceRegion)?.name}
                          </span>
                          <svg className="w-5 h-5 drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                          </svg>
                          <span className="text-lg font-bold tracking-tight drop-shadow-lg">
                            {AVAILABLE_REGIONS.find((r) => r.code === destinationRegion)?.emoji}
                            {AVAILABLE_REGIONS.find((r) => r.code === destinationRegion)?.name}
                          </span>
                        </div>
                        
                        {/* Bottom accent line */}
                        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-blue-400 to-transparent opacity-50"></div>
                      </div>
                    </div>
                  )}

                  {/* Update Options - Absolute positioned at bottom */}
                  {sourceRegion && destinationRegion && (
                    <div className="absolute bottom-4 right-4 flex flex-col space-y-2">
                      <button
                        onClick={() => setUpdatePageContent(!updatePageContent)}
                        className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                          updatePageContent
                            ? 'bg-blue-600 text-white shadow-md hover:bg-blue-700'
                            : 'bg-slate-200 text-slate-700 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600'
                        }`}
                        title="Regenerate all page content using AI (products, title, descriptions, meta tags, keywords) to create a unique SEO-optimized page for the destination region"
                      >
                        <div
                          className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                            updatePageContent ? 'border-white bg-white' : 'border-slate-400 dark:border-slate-500'
                          }`}
                        >
                          {updatePageContent && (
                            <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <span>Regenerate Page Content</span>
                      </button>

                      <button
                        onClick={() => setUpdateSearchTerms(!updateSearchTerms)}
                        className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                          updateSearchTerms
                            ? 'bg-green-600 text-white shadow-md hover:bg-green-700'
                            : 'bg-slate-200 text-slate-700 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600'
                        }`}
                        title="Generate new search terms during migration using AI to optimize for destination region"
                      >
                        <div
                          className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                            updateSearchTerms ? 'border-white bg-white' : 'border-slate-400 dark:border-slate-500'
                          }`}
                        >
                          {updateSearchTerms && (
                            <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <span>Update Search Terms</span>
                      </button>

                      <div className="flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium bg-slate-200 dark:bg-slate-700">
                        <span className="text-slate-700 dark:text-slate-300 font-medium">Force Overwrite:</span>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              setForceOverwrite(false);
                              setForceOverwriteNonEnglish(false);
                            }}
                            className={`px-2.5 py-1 rounded text-xs font-medium transition-all ${
                              !forceOverwrite && !forceOverwriteNonEnglish
                                ? 'bg-slate-600 text-white'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-300 dark:bg-slate-600 dark:text-slate-300 dark:hover:bg-slate-500'
                            }`}
                            title="Don't force overwrite - only update pages that need it"
                          >
                            Off
                          </button>
                          <button
                            onClick={() => {
                              setForceOverwrite(false);
                              setForceOverwriteNonEnglish(true);
                            }}
                            className={`px-2.5 py-1 rounded text-xs font-medium transition-all ${
                              forceOverwriteNonEnglish && !forceOverwrite
                                ? 'bg-orange-600 text-white'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-300 dark:bg-slate-600 dark:text-slate-300 dark:hover:bg-slate-500'
                            }`}
                            title="Force overwrite only non-English pages (leaves English pages untouched)"
                          >
                            Non-EN
                          </button>
                          <button
                            onClick={() => {
                              setForceOverwrite(true);
                              setForceOverwriteNonEnglish(false);
                            }}
                            className={`px-2.5 py-1 rounded text-xs font-medium transition-all ${
                              forceOverwrite
                                ? 'bg-red-600 text-white'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-300 dark:bg-slate-600 dark:text-slate-300 dark:hover:bg-slate-500'
                            }`}
                            title="Force overwrite ALL pages (English and non-English)"
                          >
                            All
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Migration Actions */}
              <div className="mb-8">
                <div className="grid gap-4 md:grid-cols-3">
                  {/* Test Suite */}
                  <div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900 p-6 text-white shadow-2xl transition-all hover:scale-105 hover:shadow-xl">
                    {/* Glass-morphism overlay */}
                    <div className="absolute inset-0 bg-gradient-to-br from-white/[0.08] to-transparent"></div>
                    
                    {/* Subtle grid pattern */}
                    <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(circle_at_50%_50%,white_1px,transparent_1px)] bg-[length:20px_20px]"></div>
                    
                    <div className="relative z-10">
                      <div className="mb-3 flex items-center space-x-3">
                        {/* Icon with enhanced styling */}
                        <div className="relative flex h-10 w-10 items-center justify-center">
                          {/* Pulsing background glow */}
                          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-400 opacity-20 blur-md"></div>
                          
                          {/* Icon background */}
                          <div className="relative flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500/30 to-teal-500/30 backdrop-blur-sm border border-white/10 shadow-lg">
                            <svg className="h-5 w-5 text-white drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2.5}
                                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                              />
                            </svg>
                          </div>
                        </div>
                        <h4 className="text-lg font-bold tracking-tight drop-shadow-lg">Test Suite</h4>
                      </div>
                      <p className="mb-4 text-emerald-100">
                        {sourceRegion && destinationRegion
                          ? `Test ${sourceRegion} → ${destinationRegion} migration`
                          : 'Select regions to run targeted tests'}
                      </p>

                      <button
                        onClick={handleRunTestSuite}
                        disabled={isRunningTests || !sourceRegion || !destinationRegion}
                        className="w-full rounded-lg bg-white/20 px-4 py-3 font-semibold text-white backdrop-blur-sm transition-all hover:bg-white/30 disabled:bg-slate-500/20 disabled:cursor-not-allowed disabled:opacity-50 border border-white/10"
                      >
                        {isRunningTests ? (
                          <div className="flex items-center justify-center space-x-2">
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                            <span>Running Tests...</span>
                          </div>
                        ) : !sourceRegion || !destinationRegion ? (
                          'Select Regions First'
                        ) : (
                          `Test ${sourceRegion} → ${destinationRegion}`
                        )}
                      </button>
                    </div>
                    
                    {/* Bottom accent line */}
                    <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-emerald-400 to-transparent opacity-50"></div>
                    
                    <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 transition-opacity group-hover:opacity-100"></div>
                  </div>

                  {/* Test Migration */}
                  <div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-6 text-white shadow-2xl transition-all hover:scale-105 hover:shadow-xl">
                    {/* Glass-morphism overlay */}
                    <div className="absolute inset-0 bg-gradient-to-br from-white/[0.08] to-transparent"></div>
                    
                    {/* Subtle grid pattern */}
                    <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(circle_at_50%_50%,white_1px,transparent_1px)] bg-[length:20px_20px]"></div>
                    
                    <div className="relative z-10">
                      <div className="mb-3 flex items-center space-x-3">
                        {/* Icon with enhanced styling */}
                        <div className="relative flex h-10 w-10 items-center justify-center">
                          {/* Pulsing background glow */}
                          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-400 to-cyan-400 opacity-20 blur-md"></div>
                          
                          {/* Icon background */}
                          <div className="relative flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500/30 to-cyan-500/30 backdrop-blur-sm border border-white/10 shadow-lg">
                            <svg className="h-5 w-5 text-white drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2.5}
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                          </div>
                        </div>
                        <h4 className="text-lg font-bold tracking-tight drop-shadow-lg">Safe Test Run</h4>
                      </div>
                      <p className="mb-4 text-blue-100">Test migration on a single document to verify the process</p>

                      {/* Document ID Input */}
                      <div className="mb-3">
                        <input
                          id="docId"
                          type="text"
                          value={testDocumentId}
                          onChange={(e) => setTestDocumentId(e.target.value)}
                          placeholder={`e.g., beauty-personal-care-${sourceRegion}`}
                          className="w-full rounded-lg border border-white/20 bg-white/10 px-4 py-3 text-white placeholder-white/70 shadow-sm transition-colors focus:border-white/40 focus:outline-none focus:ring-2 focus:ring-white/20 backdrop-blur-sm"
                          disabled={isRunning}
                        />
                      </div>

                      {/* Quick Test Buttons */}
                      <div className="mb-3 flex gap-2">
                        <button
                          onClick={() => setTestDocumentId(`144hz-monitors-en-${sourceRegion}`)}
                          disabled={isRunning}
                          className="flex-1 rounded-lg bg-white/10 px-3 py-2 text-xs font-medium text-white backdrop-blur-sm transition-all hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-50"
                          title="Test with English version (144Hz Monitors)"
                        >
                          🇬🇧 EN Test
                        </button>
                        <button
                          onClick={() => setTestDocumentId(`144hz-monitors-fr-${sourceRegion}`)}
                          disabled={isRunning}
                          className="flex-1 rounded-lg bg-white/10 px-3 py-2 text-xs font-medium text-white backdrop-blur-sm transition-all hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-50"
                          title="Test with French version (144Hz Monitors)"
                        >
                          🇫🇷 FR Test
                        </button>
                      </div>

                      {/* Test Both Languages Button */}
                      <button
                        onClick={handleTestBothLanguages}
                        disabled={isRunning || !sourceRegion || !destinationRegion}
                        className="w-full mb-3 rounded-lg bg-gradient-to-r from-emerald-600/30 to-teal-600/30 border border-emerald-400/30 px-4 py-2.5 text-sm font-semibold text-white backdrop-blur-sm transition-all hover:from-emerald-600/40 hover:to-teal-600/40 disabled:bg-slate-500/20 disabled:cursor-not-allowed disabled:opacity-50"
                        title="Test English-first migration strategy: migrates EN then FR (FR should reuse EN products if 'Regenerate Page Content' is enabled)"
                      >
                        {isRunning && currentAction.includes('Both Languages') ? (
                          <div className="flex items-center justify-center space-x-2">
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                            <span>Testing EN→FR...</span>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center space-x-2">
                            <span>🇬🇧→🇫🇷</span>
                            <span>Test Both (EN-First)</span>
                          </div>
                        )}
                      </button>

                      <button
                        onClick={handleTestSingleDocument}
                        disabled={isRunning || !testDocumentId.trim() || !sourceRegion || !destinationRegion}
                        className="w-full rounded-lg bg-white/20 px-4 py-3 font-semibold text-white backdrop-blur-sm transition-all hover:bg-white/30 disabled:bg-slate-500/20 disabled:cursor-not-allowed border border-white/10"
                      >
                        {isRunning && currentAction.includes('Test Document') ? (
                          <div className="flex items-center justify-center space-x-2">
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                            <span>Testing Document...</span>
                          </div>
                        ) : (
                          'Test Single Document'
                        )}
                      </button>
                    </div>
                    
                    {/* Bottom accent line */}
                    <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-blue-400 to-transparent opacity-50"></div>
                    
                    <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 transition-opacity group-hover:opacity-100"></div>
                  </div>

                  {/* Full Migration */}
                  <div className="group relative overflow-hidden rounded-xl p-[2px] shadow-2xl transition-all duration-500 hover:scale-[1.02] hover:shadow-3xl">
                    {/* Animated gradient border */}
                    <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 opacity-75"></div>
                    <div className="absolute inset-0 bg-gradient-to-r from-pink-500 via-red-500 to-indigo-500 opacity-50 blur-xl"></div>

                    <div className="relative h-full bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 rounded-lg p-8 text-white overflow-hidden">
                      {/* Background pattern */}
                      <div className="absolute inset-0 opacity-10">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_25%,white_2px,transparent_2px)] bg-[length:24px_24px]"></div>
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_75%,white_1px,transparent_1px)] bg-[length:16px_16px]"></div>
                      </div>

                      {/* Floating orbs */}
                      <div className="absolute top-4 left-8 w-3 h-3 bg-indigo-400 rounded-full opacity-60 animate-pulse"></div>
                      <div
                        className="absolute top-8 right-12 w-2 h-2 bg-purple-400 rounded-full opacity-60 animate-pulse"
                        style={{ animationDelay: '0.7s' }}
                      ></div>
                      <div
                        className="absolute bottom-8 left-16 w-2 h-2 bg-pink-400 rounded-full opacity-60 animate-pulse"
                        style={{ animationDelay: '1.2s' }}
                      ></div>
                      <div
                        className="absolute bottom-4 right-6 w-3 h-3 bg-red-400 rounded-full opacity-60 animate-pulse"
                        style={{ animationDelay: '1.8s' }}
                      ></div>

                      {/* Moving light beams */}
                      <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-white to-transparent opacity-30"></div>
                      <div className="absolute bottom-0 right-0 w-full h-0.5 bg-gradient-to-l from-transparent via-white to-transparent opacity-30"></div>

                      <div className="relative z-10">
                        <div className="mb-4 flex items-center space-x-4">
                          <div className="relative">
                            <div className="rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 p-3 shadow-lg">
                              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 animate-pulse"></div>
                              <svg className="relative h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M13 10V3L4 14h7v7l9-11h-7z"
                                />
                              </svg>
                            </div>
                          </div>
                          <div>
                            <h4 className="text-xl font-bold tracking-wide">Full Migration</h4>
                            <div className="flex space-x-1 mt-1">
                              <div className="w-1 h-4 bg-gradient-to-t from-indigo-500 to-purple-500 rounded-full animate-pulse"></div>
                              <div
                                className="w-1 h-4 bg-gradient-to-t from-purple-500 to-pink-500 rounded-full animate-pulse"
                                style={{ animationDelay: '0.2s' }}
                              ></div>
                              <div
                                className="w-1 h-4 bg-gradient-to-t from-pink-500 to-red-500 rounded-full animate-pulse"
                                style={{ animationDelay: '0.4s' }}
                              ></div>
                              <div
                                className="w-1 h-4 bg-gradient-to-t from-red-500 to-orange-500 rounded-full animate-pulse"
                                style={{ animationDelay: '0.6s' }}
                              ></div>
                            </div>
                          </div>
                        </div>

                        <p className="mb-6 text-slate-200 text-lg leading-relaxed">
                          Migrate all content from {sourceRegion || '[SOURCE]'} to {destinationRegion || '[DESTINATION]'} format
                          with advanced transformation protocols
                        </p>

                        {/* Split Button Container */}
                        <div className="migration-button-container relative w-full">
                          {!isSplitButtonExpanded ? (
                            // Initial Single Button
                            <button
                              onClick={() => {
                                if (!sourceRegion || !destinationRegion) {
                                  alert('Please select both source and destination regions first');
                                  return;
                                }
                                setIsSplitButtonExpanded(true);
                              }}
                              disabled={isRunning}
                              className={`group/btn relative w-full overflow-hidden rounded-lg px-4 py-3 font-semibold transition-all duration-500 ${
                                !sourceRegion || !destinationRegion
                                  ? 'bg-slate-700/50 text-slate-400 border border-slate-600 hover:bg-slate-600/50 hover:border-slate-500'
                                  : 'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white shadow-xl hover:shadow-2xl hover:from-indigo-500 hover:via-purple-500 hover:to-pink-500'
                              } ${isRunning ? 'opacity-75 cursor-not-allowed' : 'hover:scale-[1.02] active:scale-[0.98]'}`}
                            >
                              {/* Button background effects */}
                              {sourceRegion && destinationRegion && !isRunning && (
                                <>
                                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -translate-x-full group-hover/btn:translate-x-full transition-transform duration-700"></div>
                                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                </>
                              )}

                              <div className="relative flex items-center justify-center space-x-3">
                                {!sourceRegion || !destinationRegion ? (
                                  <span className="text-lg">Select Regions First</span>
                                ) : (
                                  <>
                                    <span className="text-lg tracking-wide">Choose Migration Type</span>
                                    <svg className="w-5 h-5 transition-transform group-hover/btn:rotate-180 duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </>
                                )}
                              </div>
                            </button>
                          ) : (
                            // Split into Two Buttons with Animation
                            <div className="grid grid-cols-2 gap-3 animate-in fade-in duration-500">
                              {/* Full Migration Button */}
                              <button
                                onClick={() => {
                                  if (!isConfirmingMigration || migrateTranslationsOnly) {
                                    setIsConfirmingMigration(true);
                                    setMigrateTranslationsOnly(false);
                                    setTimeout(() => setIsConfirmingMigration(false), 4000);
                                    return;
                                  }
                                  setIsConfirmingMigration(false);
                                  setIsSplitButtonExpanded(false);
                                  handleRunMigration(false);
                                }}
                                disabled={isRunning}
                                className={`group/btn relative overflow-hidden rounded-lg px-4 py-3 font-semibold transition-all duration-500 ${
                                  isConfirmingMigration && !migrateTranslationsOnly
                                    ? 'bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 text-white shadow-2xl border-2 border-amber-400/50 scale-105'
                                    : 'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white shadow-xl hover:shadow-2xl hover:from-indigo-500 hover:via-purple-500 hover:to-pink-500'
                                } ${isRunning ? 'opacity-75 cursor-not-allowed' : 'hover:scale-105 active:scale-95'}`}
                                style={{ animation: 'slideInLeft 0.5s ease-out' }}
                              >
                                {isConfirmingMigration && !migrateTranslationsOnly && !isRunning && (
                                  <div className="absolute -inset-1 bg-gradient-to-r from-amber-400 via-orange-400 to-red-400 rounded-lg blur opacity-30 animate-pulse"></div>
                                )}
                                
                                <div className="relative flex flex-col items-center space-y-1">
                                  {isRunning && currentAction === 'Full Migration' ? (
                                    <>
                                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white"></div>
                                      <span className="text-sm">Processing...</span>
                                    </>
                                  ) : isConfirmingMigration && !migrateTranslationsOnly ? (
                                    <>
                                      <svg className="h-5 w-5 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                      </svg>
                                      <span className="text-sm font-bold">Confirm</span>
                                    </>
                                  ) : (
                                    <>
                                      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      </svg>
                                      <span className="text-sm font-semibold">Full Migration</span>
                                      <span className="text-xs opacity-80">All Languages</span>
                                    </>
                                  )}
                                </div>
                              </button>

                              {/* Translations Only Button */}
                              <button
                                onClick={() => {
                                  if (!isConfirmingMigration || !migrateTranslationsOnly) {
                                    setIsConfirmingMigration(true);
                                    setMigrateTranslationsOnly(true);
                                    setTimeout(() => setIsConfirmingMigration(false), 4000);
                                    return;
                                  }
                                  setIsConfirmingMigration(false);
                                  setIsSplitButtonExpanded(false);
                                  handleRunMigration(true);
                                }}
                                disabled={isRunning}
                                className={`group/btn relative overflow-hidden rounded-lg px-4 py-3 font-semibold transition-all duration-500 ${
                                  isConfirmingMigration && migrateTranslationsOnly
                                    ? 'bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 text-white shadow-2xl border-2 border-amber-400/50 scale-105'
                                    : 'bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 text-white shadow-xl hover:shadow-2xl hover:from-emerald-500 hover:via-teal-500 hover:to-cyan-500'
                                } ${isRunning ? 'opacity-75 cursor-not-allowed' : 'hover:scale-105 active:scale-95'}`}
                                style={{ animation: 'slideInRight 0.5s ease-out' }}
                              >
                                {isConfirmingMigration && migrateTranslationsOnly && !isRunning && (
                                  <div className="absolute -inset-1 bg-gradient-to-r from-amber-400 via-orange-400 to-red-400 rounded-lg blur opacity-30 animate-pulse"></div>
                                )}
                                
                                <div className="relative flex flex-col items-center space-y-1">
                                  {isRunning && currentAction === 'Translations Migration' ? (
                                    <>
                                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white"></div>
                                      <span className="text-sm">Processing...</span>
                                    </>
                                  ) : isConfirmingMigration && migrateTranslationsOnly ? (
                                    <>
                                      <svg className="h-5 w-5 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                      </svg>
                                      <span className="text-sm font-bold">Confirm</span>
                                    </>
                                  ) : (
                                    <>
                                      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                                      </svg>
                                      <span className="text-sm font-semibold">Translations Only</span>
                                      <span className="text-xs opacity-80">Skip English</span>
                                    </>
                                  )}
                                </div>
                              </button>
                            </div>
                          )}
                          
                          {/* Back/Cancel Button when expanded */}
                          {isSplitButtonExpanded && (
                            <button
                              onClick={() => {
                                setIsSplitButtonExpanded(false);
                                setIsConfirmingMigration(false);
                              }}
                              className="mt-3 w-full text-center text-sm text-slate-300 hover:text-white transition-colors duration-200 py-2"
                            >
                              ← Back to single option
                            </button>
                          )}
                        </div>

                        <style jsx>{`
                          @keyframes slideInLeft {
                            from {
                              opacity: 0;
                              transform: translateX(-20px);
                            }
                            to {
                              opacity: 1;
                              transform: translateX(0);
                            }
                          }
                          
                          @keyframes slideInRight {
                            from {
                              opacity: 0;
                              transform: translateX(20px);
                            }
                            to {
                              opacity: 1;
                              transform: translateX(0);
                            }
                          }
                        `}</style>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Results Section */}
              {migrationResults.length > 0 && (
                <div>
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">📊 Results</h3>
                    <button
                      onClick={clearResults}
                      className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                    >
                      Clear All
                    </button>
                  </div>
                  <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
                    {migrationResults.map((result, index) => (
                      <div
                        key={index}
                        className={`group rounded-xl border shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden ${
                          result.success
                            ? 'border-green-200/60 bg-gradient-to-br from-green-50 to-emerald-50/30 dark:border-green-800/40 dark:from-green-900/10 dark:to-emerald-900/5'
                            : 'border-red-200/60 bg-gradient-to-br from-red-50 to-rose-50/30 dark:border-red-800/40 dark:from-red-900/10 dark:to-rose-900/5'
                        }`}
                      >
                        {/* Header Bar */}
                        <div className={`px-4 py-2.5 border-b ${result.success ? 'bg-green-100/50 border-green-200/60 dark:bg-green-900/20 dark:border-green-800/40' : 'bg-red-100/50 border-red-200/60 dark:bg-red-900/20 dark:border-red-800/40'}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2.5">
                              <div
                                className={`rounded-lg p-1.5 ${result.success ? 'bg-green-500/10 text-green-600 dark:text-green-400' : 'bg-red-500/10 text-red-600 dark:text-red-400'}`}
                              >
                                {result.success ? (
                                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                  </svg>
                                ) : (
                                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                )}
                              </div>
                              <h4 className={`text-sm font-semibold ${result.success ? 'text-green-900 dark:text-green-100' : 'text-red-900 dark:text-red-100'}`}>
                                {result.action}
                              </h4>
                            </div>
                            {result.timestamp && (
                              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 tabular-nums">
                                {result.timestamp.toLocaleTimeString()}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Content */}
                        <div className="p-4 space-y-3">
                          {/* Status Message */}
                          <p className={`text-sm leading-relaxed ${result.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                            {result.message || result.error || 'Operation completed'}
                          </p>

                          {/* Visit Page Button */}
                          {(result.data?.transformation?.transformedContent?.urlMapping?.href || result.data?.existingHref) && (
                            <div className="pt-1">
                              {Object.entries(
                                result.data?.transformation?.transformedContent?.urlMapping?.href || result.data?.existingHref || {}
                              ).slice(0, 1).map(
                                ([locale, url]) => (
                                  <a
                                    key={locale}
                                    href={url as string}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium transition-colors duration-150 shadow-sm hover:shadow group/btn"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                    <span>Visit Page</span>
                                    <span className="px-1.5 py-0.5 bg-blue-600/30 rounded text-xs font-mono">{locale}</span>
                                  </a>
                                ),
                              )}
                            </div>
                          )}

                          {/* All Locale Links */}
                          {(result.data?.transformation?.transformedContent?.urlMapping?.href || result.data?.existingHref) && (
                            <div className={`pt-2 border-t ${result.success ? 'border-green-200/50 dark:border-green-800/30' : 'border-gray-200/50 dark:border-gray-700/30'}`}>
                              <p className={`text-xs font-semibold mb-2.5 flex items-center gap-1.5 ${result.success ? 'text-green-800 dark:text-green-200' : 'text-gray-700 dark:text-gray-300'}`}>
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                </svg>
                                All Available Locales
                              </p>
                              <div className="grid gap-2">
                                {Object.entries(
                                  result.data?.transformation?.transformedContent?.urlMapping?.href || result.data?.existingHref || {}
                                ).map(
                                  ([locale, url]) => (
                                    <div key={locale} className="flex items-center gap-2.5 group/locale">
                                      <span className="text-xs font-mono font-semibold bg-white dark:bg-gray-800 px-2.5 py-1 rounded-md border border-gray-200 dark:border-gray-700 min-w-[60px] text-center">
                                        {locale}
                                      </span>
                                      <a
                                        href={url as string}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 underline decoration-1 underline-offset-2 hover:decoration-2 break-all transition-all"
                                      >
                                        {url as string}
                                      </a>
                                    </div>
                                  ),
                                )}
                              </div>
                            </div>
                          )}

                          {/* Transformations Applied */}
                          {result.success &&
                            result.data?.transformation?.changes &&
                            result.data.transformation.changes.length > 0 && (
                              <div className="pt-2 border-t border-green-200/50 dark:border-green-800/30">
                                <p className="text-xs font-semibold text-green-800 dark:text-green-200 mb-2.5 flex items-center gap-1.5">
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                  </svg>
                                  {result.data.transformation.changes.length} Transformation{result.data.transformation.changes.length !== 1 ? 's' : ''} Applied
                                </p>
                                <ul className="space-y-1.5">
                                  {result.data.transformation.changes.slice(0, 5).map((change: string, idx: number) => (
                                    <li key={idx} className="flex items-start gap-2 text-xs text-green-700 dark:text-green-300">
                                      <span className="text-green-500 dark:text-green-400 mt-0.5 flex-shrink-0">•</span>
                                      <span className="leading-relaxed">{change}</span>
                                    </li>
                                  ))}
                                  {result.data.transformation.changes.length > 5 && (
                                    <li className="text-xs text-green-600 dark:text-green-400 italic pl-4">
                                      + {result.data.transformation.changes.length - 5} more...
                                    </li>
                                  )}
                                </ul>
                              </div>
                            )}

                          {/* Migration Summary Stats */}
                          {result.success && result.data?.successful && (
                            <div className="pt-2 border-t border-green-200/50 dark:border-green-800/30">
                              <p className="text-xs font-semibold text-green-800 dark:text-green-200 mb-2.5 flex items-center gap-1.5">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                                Migration Statistics
                              </p>
                              <div className="grid grid-cols-3 gap-2">
                                <div className="bg-white dark:bg-gray-800 rounded-lg px-3 py-2 border border-green-200/50 dark:border-green-800/30">
                                  <div className="text-xs text-green-600 dark:text-green-400 font-medium">Successful</div>
                                  <div className="text-lg font-bold text-green-700 dark:text-green-300 tabular-nums">{result.data.successful}</div>
                                </div>
                                {result.data.failed > 0 && (
                                  <div className="bg-white dark:bg-gray-800 rounded-lg px-3 py-2 border border-red-200/50 dark:border-red-800/30">
                                    <div className="text-xs text-red-600 dark:text-red-400 font-medium">Failed</div>
                                    <div className="text-lg font-bold text-red-700 dark:text-red-300 tabular-nums">{result.data.failed}</div>
                                  </div>
                                )}
                                <div className="bg-white dark:bg-gray-800 rounded-lg px-3 py-2 border border-gray-200/50 dark:border-gray-700">
                                  <div className="text-xs text-gray-600 dark:text-gray-400 font-medium">Total</div>
                                  <div className="text-lg font-bold text-gray-700 dark:text-gray-300 tabular-nums">{result.data.totalProcessed}</div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Technical Details */}
                          {result.data && (
                            <details className="pt-2 border-t border-gray-200/50 dark:border-gray-700/30 group/details">
                              <summary className="cursor-pointer text-xs font-semibold text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 transition-colors flex items-center gap-1.5 select-none">
                                <svg className="w-3.5 h-3.5 transition-transform group-open/details:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                                Technical Details
                              </summary>
                              <div className="mt-2.5 rounded-lg bg-green-800/70 dark:bg-black p-3 border border-gray-200 dark:border-gray-800">
                                <pre className="text-xs text-gray-100 dark:text-gray-300 overflow-x-auto font-mono leading-relaxed">
                                  {JSON.stringify(result.data, null, 2)}
                                </pre>
                              </div>
                            </details>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Test Suite Results */}
              {showTestResults && (
                <div className="mb-8">
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">🧪 Test Suite Results</h3>
                    <button
                      onClick={() => setShowTestResults(false)}
                      className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                    >
                      Hide Results
                    </button>
                  </div>
                  <div className="rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6">
                    <MigrationTestResults results={testResults} loading={isRunningTests} />
                  </div>
                </div>
              )}

              {/* Current Action Indicator */}
              {isRunning && (
                <div className="fixed bottom-6 right-6 z-50">
                  <div className="rounded-full bg-blue-600 px-6 py-3 text-white shadow-lg">
                    <div className="flex items-center space-x-3">
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      <span className="font-medium">{currentAction}...</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Backward compatibility export
export const MigrateCanadaToUSA = UniversalRegionMigrationTool;
